import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "src/Redux/store";
import { RootState } from "src/Redux/Slices";
import { addItem, extendTripToInclude, selectTripPlanner } from "src/Redux/Slices/tripPlanner";
import { resolveItemProductClass } from "src/Utilities/tripPlannerHelpers";
import {
    AllFaresAvailabilityPanel,
    TripPanelSelection,
} from "src/Module/Checkout/Components/AllFaresAvailabilityPanel";
import { getProductHelper } from "src/Module/Checkout/productHelper";
import { IProductDetail } from "@interfaces";

/**
 * Best-effort number-of-days for a product. The TDMS payload uses string
 * fields and may only populate `durationNight`. Always returns >= 1.
 */
const deriveProductDays = (p: IProductDetail): number => {
    const days = Number(p.durationDays);
    const nights = Number(p.durationNight);
    // Mirror `deriveSpanDays` in productSidebarMapper: a trip with N nights
    // occupies N+1 calendar days, so a "2 Day 2 Night" tour (itinerary
    // Day 1→Day 3) spans 3. Take the larger of the day count and nights+1.
    const daySpan = Number.isFinite(days) && days > 0 ? days : 0;
    const nightSpan = Number.isFinite(nights) && nights > 0 ? nights + 1 : 0;
    return Math.max(1, daySpan, nightSpan);
};

interface Props {
    open: boolean;
    productId: string;
    productName: string;
    /**
     * Seed the availability panel to a specific check-in date (YYYY-MM-DD).
     * Used by the Trip Planner when accommodation is dropped/tapped onto a
     * day: the picker opens focused on that day so the user prices the room
     * for the day they chose. Falls back to the trip's date window.
     */
    initialDate?: string;
    /**
     * Canonical productClass to persist on the added item, overriding the
     * re-fetched TDMS detail's raw class. The accommodation room picker passes
     * the sidebar-normalised `"accommodation"` here so the item is classed
     * consistently (and gets the overnight house token) even though the modal
     * re-fetches the product detail to price the room.
     */
    productClassOverride?: string | null;
    onClose: () => void;
}

/**
 * Reuses the existing product-page availability panel inside a modal so the
 * user can preview real availability before adding products to the planner.
 * In trip mode, the panel renders an "Add to Trip" button (instead of Add to
 * Cart / Book Now). When the user clicks it, each selected fare row is
 * dispatched as an `addItem` action onto the trip's day index that
 * corresponds to the picked date — so the live cart is never touched and
 * the existing checkout flow keeps working unchanged.
 */
export const AvailabilityModal = ({
    open,
    productId,
    productName,
    initialDate,
    productClassOverride,
    onClose,
}: Props) => {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const { current: trip } = useAppSelector(selectTripPlanner);
    const [product, setProduct] = useState<IProductDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initialDateRange = useMemo(() => {
        // A dropped/tapped day takes priority: focus the panel on that exact
        // date so the user sees (and prices) the room rates for the day they
        // chose, rather than the whole trip window.
        if (initialDate) {
            const start = moment(initialDate, "YYYY-MM-DD").toDate();
            return { start, end: start };
        }
        if (!trip?.startDate || !trip?.durationDays) return null;
        const start = moment(trip.startDate, "YYYY-MM-DD").toDate();
        const end = moment(trip.startDate, "YYYY-MM-DD")
            .add(trip.durationDays - 1, "days")
            .toDate();
        return { start, end };
    }, [initialDate, trip?.startDate, trip?.durationDays]);

    useEffect(() => {
        if (!open || product) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        // skipMedia=true keeps the popup snappy; the panel doesn't need images.
        getProductHelper(productId, true)
            .then((res) => {
                if (cancelled) return;
                if (res.isSuccess && res.data) {
                    setProduct(res.data);
                } else {
                    setError(res.errorMessage || "Could not load availability.");
                }
            })
            .catch(() => {
                if (!cancelled) setError("Could not load availability.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, productId, product]);

    const handleAddToTrip = (sel: TripPanelSelection) => {
        if (!product) return;
        // The panel emits the date in DD-MMM-YYYY (its own internal format).
        // We need a zero-based dayIndex relative to the trip's startDate.
        const picked = moment(sel.date, "DD-MMM-YYYY").startOf("day");
        const pickedIso = picked.format("YYYY-MM-DD");
        // The item's full calendar footprint (e.g. 3 for a "2 Day 2 Night"
        // tour). We must grow the trip to fit the WHOLE span, not just the
        // picked start date — otherwise picking a date at/after the trip end
        // makes that date the last day, and addItem's clamp
        // (maxSpan = durationDays - dayIndex) collapses the tour to a single
        // day. Computed once and reused for the addItem payload below.
        // Canonical class for this item (accommodation picker passes the
        // normalised "accommodation" override). Computed up front because it
        // also drives the minimum calendar span below.
        const resolvedClass = resolveItemProductClass(
            productClassOverride,
            product.productClass,
        );
        // Accommodation always covers at least one overnight, so it must span
        // at least 2 calendar days (check-in + check-out) even when the
        // re-fetched TDMS detail leaves durationNight/durationDays empty (which
        // collapses deriveProductDays to 1). Multi-night rooms keep their
        // larger nights+1 span.
        const isAccommodationItem = resolvedClass === "accommodation";
        const span = Math.max(
            deriveProductDays(product),
            isAccommodationItem ? 2 : 1,
        );
        const endIso = picked.clone().add(span - 1, "days").format("YYYY-MM-DD");
        // If the user picked a date outside the current trip window
        // (the panel's date picker can be scrolled past the seeded range),
        // grow the trip so BOTH the picked start AND the item's end fall
        // inside it. Capped at 40 days inside the slice.
        dispatch(extendTripToInclude({ date: pickedIso }));
        dispatch(extendTripToInclude({ date: endIso }));
        // Re-read the (possibly extended) trip from the freshly-dispatched
        // state. We can't trust the closed-over `trip.startDate` because
        // the extension above may have shifted it earlier.
        const refreshed = store.getState().tripPlanner.current;
        const start = moment(refreshed.startDate, "YYYY-MM-DD").startOf("day");
        const dayIndex = picked.diff(start, "days");
        if (dayIndex < 0 || dayIndex >= refreshed.durationDays) {
            // Cap (40 days) prevented the extension. Bail gracefully — the
            // user can shorten the trip or pick a closer date.
            return;
        }
        const tdmsId = product.tdms_product_id ?? Number(product.productId);
        dispatch(
            addItem({
                productId: String(product.productId),
                tdmsProductId: Number(tdmsId) || 0,
                name: product.name || productName,
                productImagePath: product.productImagePath,
                productClass: resolvedClass,
                fareTypeId: sel.fareTypeId,
                fareName: sel.fareName,
                productPricesDetailsId: sel.productPricesDetailsId,
                // Capture the availability-endpoint discriminator so a later
                // drag-drop move re-checks this room on the SAME endpoint the
                // calendar just used (SiteMinder accommodation needs
                // checkAvailabilityByProductAndRange, not the range endpoint).
                apiProviderId: product.apiProviderId,
                groupFaresForAvailabilityCheck: product.groupFaresForAvailabilityCheck,
                rrp: sel.rrp,
                numPax: sel.numPax,
                quantity: sel.quantity,
                bookingTimeId: sel.bookingTimeId,
                dayIndex,
                // Carry the product's own duration so multi-day tours render
                // as a span (matching the drag-and-drop path). The addItem
                // reducer clamps to maxSpan = trip.durationDays - dayIndex,
                // so an item near the end of the trip is shortened rather
                // than overflowing past the trip window.
                //
                // IDetailTour.durationDays is typed as a string and is "0"
                // (or empty) for products whose duration is only stored as
                // nights. `deriveProductDays` coerces and falls back to
                // durationNight + 1 so a multi-day tour added via the modal
                // spans the same number of days as when dragged from the
                // sidebar. The trip was extended above to fit this span, so
                // addItem's clamp no longer collapses it to a single day.
                durationDays: span,
                // The user just saw availability and picked an open slot —
                // skip the auto recheck by marking it available straight away.
                availabilityStatus: "available",
                // Carry the exact row index the user picked, so the cart /
                // quote payload's `selectedAvailableIndices` points at the
                // right row in the availability response. Without this we'd
                // fall back to [0] and trip a 400 "Validation Error".
                availIndex: sel.availIndex,
            }),
        );
        // Close after a successful add so the planner is visible again.
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            PaperProps={{ sx: { overflow: "hidden" } }}
        >
            <DialogTitle sx={{ pr: 6 }}>
                {productName}
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowX: "auto" }}>
                {loading && (
                    <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                        <CircularProgress size={32} />
                    </div>
                )}
                {error && !loading && (
                    <div style={{ color: "#c62828", padding: 16 }}>{error}</div>
                )}
                {product && !loading && (
                    <AllFaresAvailabilityPanel
                        product={product}
                        layout="modal"
                        // Pass the dropped/tapped day as `initialDate` (not just
                        // the range) so the panel auto-fetches availability for
                        // that exact date on open — same behaviour as the normal
                        // product-detail checkout calendar (ProductPricings).
                        initialDate={
                            initialDate
                                ? moment(initialDate, "YYYY-MM-DD").toDate()
                                : undefined
                        }
                        initialDateRange={initialDateRange}
                        actionMode="trip"
                        onAddToTrip={handleAddToTrip}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
